import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { List } from "@mui/material";
import { useEffect, useState, type JSX } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { BindDialogReturn, DialogProps, DialogResult, DialogState, bindDialog, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useSaveUserFavorites } from "../../queries/user-favorites/UseSaveUserFavorites";
import { useUserFavorites } from "../../queries/user-favorites/UseUserFavorites";
import Form from "../forms/form/Form";
import LoadingZone from "../loading-zone/LoadingZone";
import NoData from "../no-data/NoData";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import { mergeFavorites, toUserFavoritesManagerField } from "./UserFavoritesManagerDialogUtils";
import { UserFavoritesManagerFavoriteFieldType, UserFavoritesManagerFieldType } from "./UserFavoritesManagerFieldTypes";
import { UserFavoritesManagerListItem } from "./UserFavoritesManagerListItem";
import { eLynxAppIDEnum } from "../../common/enums/eLynxAppIDEnum";
import RenameConfigurationDialog, { bindRenameConfigurationDialog, useRenameConfigurationDialogState } from "../rename-configuration-dialog/RenameConfigurationDialog";
import { useSaveUserFavorite } from "../../queries/user-favorites/UseSaveUserFavorite";
import AssignHomePageDialog, { bindAssignHomePageDialog, useAssignHomePageDialogState } from "../assign-home-page-dialog/AssignHomePageDialog";
import { useAssignUserFavorite } from "../../queries/user-favorites/UseAssignUserFavorite";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useUserFavoriteAccessibleUsers } from "../../queries/user-favorites/UseUserFavoriteAccessibleUsers";
import { IAssignUserFavoriteModel, IAssignUserFavoriteParams } from "../../services/ui-personalization-data-service/Models";
import _ from "lodash";

export type UserFavoritesManagerDialogInitValues = {
};

export type UserFavoritesManagerDialogResultValues = {
};

export type UserFavoritesManagerDialogResult = DialogResult<UserFavoritesManagerDialogResultValues>;

export type UserFavoritesManagerDialogProps = DialogProps<UserFavoritesManagerDialogInitValues, UserFavoritesManagerDialogResult>;

export default function UserFavoritesManagerDialog({
    open,
    onClose,
}: UserFavoritesManagerDialogProps): JSX.Element {

    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const { confirm, acknowledge } = useConfirmation();
    const renameConfigurationDialog = useRenameConfigurationDialogState();
    const assignHomePageDialog = useAssignHomePageDialogState();

    const {
        data,
        status
    } = useQueryNotifications(useUserFavorites)({
        enabled: open
    });

    const { mutate: saveFavorites } = useQueryNotifications(useSaveUserFavorites)({
        onSuccess: () => {
            acknowledge(t("userFavoritesManagerDialog.saveSuccessMessage"));
        }
    });


    const { mutate: assignFavorite } = useQueryNotifications(useAssignUserFavorite)({
        groupID: routeContext.groupID!,
        onSuccess: () => {
            acknowledge(t("userFavoritesManagerDialog.saveSuccessMessage"));
        }
    });

    // const { mutate: saveFavorite } = useQueryNotifications(useSaveUserFavorite)({
    //     onSuccess: () => {
    //         acknowledge(t("userFavoritesManagerDialog.saveSuccessMessage"));
    //     }
    // });

    const [draggingItem, setDraggingItem] = useState<UserFavoritesManagerFavoriteFieldType | undefined>(undefined);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 0,
                tolerance: 5
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const form = useForm<UserFavoritesManagerFieldType>({
        mode: "onChange",
        defaultValues: {
            favorites: new Array<UserFavoritesManagerFavoriteFieldType>()
        } as UserFavoritesManagerFieldType
    });

    const {
        handleSubmit,
        formState: {
            isValid,
            isDirty,
            
        },
        reset,
        watch,
        control
    } = form;

    const {
        fields: favoriteFields,
        remove: removeFavorite,
        move: moveFavorite,
        update: updateFavorite
    } = useFieldArray<UserFavoritesManagerFieldType, "favorites", "fieldID">({
        name: "favorites",
        keyName: "fieldID",
        control: control
    });

    const [watchFavorites] = watch(["favorites"]);

    const favorites = favoriteFields.map((field, index) => {
        return {
            ...field,
            ...watchFavorites[index],
            favoriteKey: `favorites.${index}`
        };
    });

    useEffect(() => {
        if (data) {
            reset(toUserFavoritesManagerField(data));
        }
    }, [
        reset,
        data
    ]);

    async function save(field: UserFavoritesManagerFieldType) {

        if (data) {
            // User Story 53103: Research: 2026 Q1 - Review GAS Report
            const favorites = mergeFavorites(data /*?? []*/, field);

            saveFavorites(favorites);

            reset();
            onClose && onClose({
                reason: "ok"
            });
        }
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getCancelConfirmation()
            : true;

        if (canCancel) {
            reset();
            onClose && onClose({
                reason: "cancel"
            });
        }
    }

    async function getCancelConfirmation() {
        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    async function getRemoveConfirmation() {
        return (await confirm({
            dialogTitle: t("userFavoritesManagerDialog.removeConfirmation.title"),
            confirmationText: t("userFavoritesManagerDialog.removeConfirmation.confirmationText"),
            yesButtonText: t("userFavoritesManagerDialog.removeConfirmation.yesButtonText"),
            noButtonText: t("userFavoritesManagerDialog.removeConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    function onDragStart(event: DragStartEvent) {
        const item = favorites.find(o => o.id === event.active.id);
        setDraggingItem(item);
    }

    function onDragEnd(event: DragEndEvent) {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = favorites.findIndex(o => o.id === active.id);
            const newIndex = favorites.findIndex(o => o.id === over?.id);

            if (oldIndex !== undefined && newIndex !== undefined) {
                moveFavorite(oldIndex, newIndex);
            }
        }

        setDraggingItem(undefined);
    }

    function onSetAsHome(item: UserFavoritesManagerFavoriteFieldType) {
        // if new home is v2, then only clear v2 favorite home flag
        // if new home is v1 then clear all favorite home flags
        favorites.forEach((favorite, index) => {
            updateFavorite(
                index,
                {
                    ...favorite,
                    isHomeUrl: item.eLynxAppID === eLynxAppIDEnum.eLynxApp2022
                        ? favorite.id === item.id || (favorite.eLynxAppID === eLynxAppIDEnum.eLynxApp && favorite.isHomeUrl)
                        : favorite.id === item.id
                }
            );
        });
    }

    async function onRemove(item: UserFavoritesManagerFavoriteFieldType) {
        const index = favorites.findIndex(o => o.id === item.id);

        const shouldRemove = await getRemoveConfirmation();

        if (shouldRemove) {
            removeFavorite(index);
        }
    }

    async function onRename(item: UserFavoritesManagerFavoriteFieldType) {

        const index = favorites.findIndex(o => o.id === item.id);

        const result = await renameConfigurationDialog.open({
            configurationName: item.description,
            configurationType: t("actions.favorite"),
            maxLength: 256,
            rows: 3,
            validate: (value: string) => {

                if (favorites.some(o => o.description === value && o.id !== item.id)) {
                    return t('userFavorites.nameUnique');
                }
                return value.trim().length > 0 || t('configuration.nameRequired');
            }
        });

        if (result.reason === "ok" && result.values) {

            //saveFavorite()
            updateFavorite(index, {...item, description: result.values!.configurationName});

        }

    }

    async function onAssignHomePage(item: UserFavoritesManagerFavoriteFieldType) {

        const userFavorite = data?.find(o => o.id === item.id);

        const result = await assignHomePageDialog.open({
            title: 'Assign Home Page',
            favorite: userFavorite!,
            assignHomePage: true
        });

        if (result.reason === 'ok') {


            const userAssignments = result.values!.users.map(o => {
                return {
                    userID: o.id,
                    homeFavorite: true,
                    createFavorite: !o.favorites?.some(v => _.isEqual(v.routeDetail, userFavorite?.routeDetail))
                } as IAssignUserFavoriteModel;
            });

            const parms: IAssignUserFavoriteParams = {
                favoriteID: item.id,
                assignments: userAssignments
            };

            assignFavorite(parms);
        }
    }
    
    async function onShare(item: UserFavoritesManagerFavoriteFieldType) {

        const userFavorite = data?.find(o => o.id === item.id);        

        const result = await assignHomePageDialog.open({
            title: 'Share Favorite',
            favorite: userFavorite!,
            assignHomePage: false
        });

        if (result.reason === 'ok') {

            const userFavorite = data?.find(o => o.id === item.id);

            const userAssignments = result.values!.users.map(o => {
                return {
                    userID: o.id,
                    homeFavorite: false,
                    createFavorite: !o.favorites?.some(v => _.isEqual(v.routeDetail, userFavorite?.routeDetail))
                } as IAssignUserFavoriteModel;
            });

            const parms: IAssignUserFavoriteParams = {
                favoriteID: item.id,
                assignments: userAssignments
            };

            assignFavorite(parms);
        }

    }


    const v2HomeFavorite = favorites?.find(f => f.isHomeUrl && f.eLynxAppID === eLynxAppIDEnum.eLynxApp2022);

    return (
        <>
            <ResponsiveDialog
                title={t("userFavoritesManagerDialog.title")}
                enableOkay={
                    isValid && isDirty
                }
                okayLabel={t("buttons.save")}
                open={open}
                onClose={
                    (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => save(data))()
                            : onCancel();
                    }
                }
                contentsx={{
                    backgroundColor: "background.paper",
                    padding: 0,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    flex: "1 1 auto",
                }}
            >
                <LoadingZone isLoading={status === "loading"}>
                    {status === "success" && favorites.length === 0 &&
                        <NoData text="No favorites are available" />
                    }
                    <Form form={form}>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                        >
                            <SortableContext
                                items={favorites.map(o => o.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <List
                                    dense
                                    sx={{

                                        display: "flex",
                                        flexDirection: "column",
                                        flex: "1 1 auto",
                                        paddingTop: 0,
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                        width: 1
                                    }}>
                                    {favorites.map(favorite =>
                                        <UserFavoritesManagerListItem
                                            key={favorite.fieldID}
                                            item={{
                                                ...favorite,
                                                isHomeUrl: favorite.isHomeUrl &&
                                                 ((favorite.eLynxAppID === eLynxAppIDEnum.eLynxApp && v2HomeFavorite === undefined) ||
                                                 (favorite.eLynxAppID === eLynxAppIDEnum.eLynxApp2022))
                                            }}
                                            elevation={0}
                                            onSetAsHome={onSetAsHome}
                                            onRemove={onRemove}
                                            onRename={onRename}
                                            onAssignHomePage={onAssignHomePage}
                                            onShare={onShare}

                                        />
                                    )}
                                </List>

                                <DragOverlay
                                    style={{
                                        touchAction: "none",
                                        cursor: "pointer",
                                    }}
                                >
                                    {draggingItem &&
                                        <UserFavoritesManagerListItem item={draggingItem} elevation={2} />
                                    }
                                </DragOverlay>
                            </SortableContext>
                        </DndContext>
                    </Form>
                </LoadingZone>

            </ResponsiveDialog>

            <RenameConfigurationDialog {...bindRenameConfigurationDialog(renameConfigurationDialog)} />
            <AssignHomePageDialog {...bindAssignHomePageDialog(assignHomePageDialog)} />
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getCancelConfirmation}
            />
        </>
    );
}

export type UserFavoritesManagerDialogState = DialogState<UserFavoritesManagerDialogInitValues, UserFavoritesManagerDialogResult>;
export type BindUserFavoritesManagerDialogReturn = BindDialogReturn<UserFavoritesManagerDialogInitValues, UserFavoritesManagerDialogResult>;

export function useUserFavoritesManagerDialogState(): UserFavoritesManagerDialogState {
    return useDialogState();
}

export function bindUserFavoritesManagerDialog(props: UserFavoritesManagerDialogState): BindUserFavoritesManagerDialogReturn {
    return bindDialog(props);
}